import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Shield, ArrowRight, FileText, DollarSign, Search, AlertTriangle,
  CheckCircle2, BarChart3, Scale, Users, Eye, Loader2
} from "lucide-react";
import { INSURANCE_SPECIALTIES } from "@/constants/legalSubjects";
import { InsuranceSpecialtySelector } from "@/components/shared/PracticeAreaSelector";

const INSURANCE_TOOLS = [
  { 
    id: "insurance-report", to: "/tools/insurance-report", 
    name: "Coverage Analysis", icon: Shield, color: "bg-blue-600",
    desc: "Analyze coverage, identify gaps, detect bad faith patterns" 
  },
  { 
    id: "medical-intel", to: "/tools/medical-intel", 
    name: "Medical Provider Intel", icon: AlertTriangle, color: "bg-red-600",
    desc: "Identify suspicious provider-attorney networks" 
  },
  { 
    id: "request-center", to: "/tools/request-center", 
    name: "Legal Request Center", icon: Search, color: "bg-emerald-600",
    desc: "Research coverage disputes, bad faith precedent" 
  },
  { 
    id: "settlement-negotiation", to: "/tools/settlement-negotiation", 
    name: "Settlement Negotiation", icon: DollarSign, color: "bg-amber-600",
    desc: "Strategy for insurance settlement negotiations" 
  },
  { 
    id: "demand-letter", to: "/tools/demand-letter", 
    name: "Demand Letters", icon: FileText, color: "bg-orange-600",
    desc: "Generate professional insurance demand letters" 
  },
  { 
    id: "investigator", to: "/tools/investigator", 
    name: "Opposition Research", icon: Eye, color: "bg-purple-600",
    desc: "Investigate opposing parties and carriers" 
  },
  { 
    id: "carrier-scorecard", to: "/tools/carrier-scorecard", 
    name: "Carrier Scorecard", icon: BarChart3, color: "bg-indigo-600",
    desc: "AI-powered carrier risk ratings & negotiation intelligence" 
  },
];

const INSURANCE_METRICS = [
  { label: "Coverage Types Tracked", value: "15+", icon: Shield },
  { label: "Carrier Analytics", value: "200+", icon: BarChart3 },
  { label: "Bad Faith Indicators", value: "50+", icon: AlertTriangle },
  { label: "States Covered", value: "50", icon: Scale },
];

const InsuranceSectionPage = () => {
  const [selectedSpecialty, setSelectedSpecialty] = useState("");

  return (
    <div className="min-h-screen bg-slate-50" data-testid="insurance-section-page">
      {/* Hero */}
      <div className="bg-gradient-to-r from-blue-900 to-indigo-900 text-white px-6 py-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-blue-800/50 rounded-xl">
              <Shield className="h-8 w-8 text-blue-300" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Insurance Intelligence Center</h1>
              <p className="text-blue-200 mt-1">Complete toolkit for insurance litigation & defense</p>
            </div>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            {INSURANCE_METRICS.map((m, i) => (
              <div key={i} className="bg-white/10 backdrop-blur rounded-lg p-4 text-center">
                <m.icon className="h-5 w-5 text-blue-300 mx-auto mb-2" />
                <p className="text-2xl font-bold">{m.value}</p>
                <p className="text-xs text-blue-200">{m.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Specialty Selector */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-600" />
              Filter by Insurance Specialty
            </CardTitle>
          </CardHeader>
          <CardContent>
            <InsuranceSpecialtySelector 
              value={selectedSpecialty} 
              onChange={setSelectedSpecialty}
              className="max-w-md"
            />
            {selectedSpecialty && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm font-medium text-blue-800">
                  {INSURANCE_SPECIALTIES.find(s => s.value === selectedSpecialty)?.label}
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  {INSURANCE_SPECIALTIES.find(s => s.value === selectedSpecialty)?.desc}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Insurance Tools Grid */}
        <div>
          <h2 className="text-lg font-bold mb-4">Insurance Tools</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {INSURANCE_TOOLS.map((tool) => (
              <Link key={tool.id} to={tool.to} data-testid={`insurance-tool-${tool.id}`}>
                <Card className="h-full hover:shadow-md hover:border-blue-300 transition-all cursor-pointer group">
                  <CardContent className="pt-6 pb-6">
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-xl ${tool.color} text-white group-hover:scale-105 transition-transform`}>
                        <tool.icon className="h-6 w-6" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-base mb-1">{tool.name}</h3>
                        <p className="text-sm text-slate-500">{tool.desc}</p>
                      </div>
                      <ArrowRight className="h-5 w-5 text-slate-400 group-hover:text-blue-600 transition-colors mt-1" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>

        {/* Insurance Specialties Overview */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">All Insurance Specialties</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {INSURANCE_SPECIALTIES.map((spec) => (
                <div 
                  key={spec.value}
                  className="p-3 border rounded-lg hover:border-blue-300 hover:bg-blue-50/50 transition-all cursor-pointer"
                  data-testid={`insurance-specialty-${spec.value}`}
                >
                  <p className="text-sm font-medium">{spec.label}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{spec.desc}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default InsuranceSectionPage;
